// UpgradePlan.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Avatar,
  Chip,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import DiamondIcon from '@mui/icons-material/Diamond';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StarsIcon from '@mui/icons-material/Stars';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
  accent: '#3b82f6',
  accentLight: 'rgba(59, 130, 246, 0.15)',
  accentGlow: 'rgba(59, 130, 246, 0.4)',
  purple: '#8b5cf6',
  purpleLight: 'rgba(139, 92, 246, 0.15)',
  gold: '#f59e0b',
  goldLight: 'rgba(245, 158, 11, 0.15)',
  text: '#e2e8f0',
  textMuted: '#64748b',
  border: 'rgba(255, 255, 255, 0.08)',
  cardBg: 'rgba(255, 255, 255, 0.03)',
};

const MAX_PLAN_ID = 4;

// Plan configurations for Business accounts
const businessPlanStyles = {
  freemium: {
    icon: RocketLaunchIcon,
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
    color: colors.accent,
    bgColor: colors.accentLight,
  },
  basic: {
    icon: AutoAwesomeIcon,
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
    color: colors.purple,
    bgColor: colors.purpleLight,
  },
  professional: {
    icon: WorkspacePremiumIcon,
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
    color: colors.gold,
    bgColor: colors.goldLight,
  },
  enterprise: {
    icon: DiamondIcon,
    gradient: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
    color: '#ec4899',
    bgColor: 'rgba(236, 72, 153, 0.15)',
  },
};

// Plan configurations for Individual accounts
const individualPlanStyles = {
  freemium: {
    icon: RocketLaunchIcon,
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
    color: colors.accent,
    bgColor: colors.accentLight,
  },
  basic: {
    icon: AutoAwesomeIcon,
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
    color: colors.purple,
    bgColor: colors.purpleLight,
  },
  professional: {
    icon: WorkspacePremiumIcon,
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
    color: colors.gold,
    bgColor: colors.goldLight,
  },
  platinum: {
    icon: StarsIcon,
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    color: '#06b6d4',
    bgColor: 'rgba(6, 182, 212, 0.15)',
  },
};

const getPlanStyle = (planName, isBusinessAccount) => {
  const name = (planName || 'freemium').toLowerCase();
  const planStyles = isBusinessAccount ? businessPlanStyles : individualPlanStyles;
  return planStyles[name] || planStyles.freemium;
};

export default function UpgradePlan() {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [teamAccountType, setTeamAccountType] = useState("individual");
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  const fetchSubscriptionData = useCallback((isUpdate = false) => {
    if (isUpdate) setIsUpdating(true);
    else setIsInitialLoading(true);

    setTimeout(() => {
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const userData = JSON.parse(userStr);

          setUserRole(userData?.active_team?.user_role_in_team || "");
          setTeamAccountType(userData?.active_team?.team_account_type || userData?.account_type || "individual");

          if (userData?.active_team?.subscription?.plan) {
            setSubscriptionData(userData.active_team.subscription);
          } else {
            setSubscriptionData({ plan: { name: "Freemium", display_name: "Freemium", id: null } });
          }
        } else {
          setSubscriptionData({ plan: { name: "Freemium", display_name: "Freemium", id: null } });
        }
      } catch (e) {
        console.error("UpgradePlan: Error parsing user data:", e);
        setError("Could not load subscription info.");
        setSubscriptionData(null);
      } finally {
        if (isUpdate) setIsUpdating(false);
        else setIsInitialLoading(false);
      }
    }, 0);
  }, []);

  useEffect(() => {
    fetchSubscriptionData(false);

    const handleLocalStorageUpdate = () => fetchSubscriptionData(true);
    window.addEventListener("userLocalStorageUpdated", handleLocalStorageUpdate);

    const handleStorageEvent = (event) => {
      if (event.key === "user" && event.storageArea === localStorage) {
        fetchSubscriptionData(true);
      }
    };
    window.addEventListener("storage", handleStorageEvent);

    return () => {
      window.removeEventListener("userLocalStorageUpdated", handleLocalStorageUpdate);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [fetchSubscriptionData]);

  if (isInitialLoading) {
    return (
      <Box sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
        minHeight: 80,
      }}>
        <CircularProgress size={20} sx={{ color: colors.accent }} />
      </Box>
    );
  }

  if (error || !subscriptionData || !subscriptionData.plan) {
    return null;
  }

  const isBusinessAccount = teamAccountType === "business";

  // Use display_name if available, otherwise fall back to name
  let currentPlanDisplayName = subscriptionData.plan.display_name || subscriptionData.plan.name || "Freemium";
  currentPlanDisplayName = currentPlanDisplayName.charAt(0).toUpperCase() + currentPlanDisplayName.slice(1);

  // Get plan name for styling (use internal name, not display name)
  let planNameForStyle = (subscriptionData.plan.name || "freemium").toLowerCase();

  // For individual accounts, map enterprise to platinum
  if (!isBusinessAccount && planNameForStyle === "enterprise") {
    planNameForStyle = "platinum";
    if (!subscriptionData.plan.display_name) {
      currentPlanDisplayName = "Platinum";
    }
  }

  const currentPlanId = subscriptionData.plan.id || 0;
  const isOnMaxPlan = currentPlanId !== null && currentPlanId === MAX_PLAN_ID;
  const isAdmin = userRole.toLowerCase() === "admin";
  const planStyle = getPlanStyle(planNameForStyle, isBusinessAccount);
  const PlanIcon = planStyle.icon;

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: '12px',
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 80,
            height: 80,
            background: planStyle.gradient,
            borderRadius: '50%',
            opacity: 0.1,
            filter: 'blur(20px)',
          }}
        />

        {/* Updating indicator */}
        {isUpdating && (
          <CircularProgress
            size={16}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: colors.textMuted
            }}
          />
        )}

        {/* Plan info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              background: planStyle.gradient,
              boxShadow: `0 4px 12px ${planStyle.color}40`,
            }}
          >
            <PlanIcon sx={{ fontSize: '1.1rem' }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily,
                fontSize: '0.65rem',
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Current Plan
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography
                sx={{
                  fontFamily,
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  color: colors.text,
                }}
              >
                {currentPlanDisplayName}
              </Typography>
              {isOnMaxPlan && (
                <Chip
                  label="MAX"
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: '0.55rem',
                    fontWeight: 700,
                    bgcolor: planStyle.bgColor,
                    color: planStyle.color,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Action area */}
        {isAdmin ? (
          isOnMaxPlan ? (
            <Typography
              sx={{
                fontFamily,
                fontSize: '0.75rem',
                color: colors.textMuted,
                textAlign: 'center',
                py: 0.5,
              }}
            >
              You're on our best plan!
            </Typography>
          ) : (
            <Button
              variant="contained"
              component={Link}
              to="/billing?tab=plan"
              fullWidth
              size="small"
              disabled={isUpdating}
              sx={{
                mt: 0.5,
                py: 0.75,
                background: planStyle.gradient,
                fontFamily,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                boxShadow: `0 4px 12px ${planStyle.color}30`,
                '&:hover': {
                  background: planStyle.gradient,
                  boxShadow: `0 6px 16px ${planStyle.color}40`,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {currentPlanDisplayName.toLowerCase() === "freemium" ? "Upgrade Plan" : "View Plans"}
            </Button>
          )
        ) : (
          <Typography
            sx={{
              fontFamily,
              fontSize: '0.7rem',
              color: colors.textMuted,
              textAlign: 'center',
              fontStyle: 'italic',
              py: 0.5,
            }}
          >
            Contact admin to upgrade
          </Typography>
        )}
      </Box>
    </Box>
  );
}
